// CoordStudentAttendance.jsx — mirrors admin StudentAttendancePage.jsx exactly
// Edit attendance → POST /coordinator/request/student-attendance-edit (not direct PUT)

import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const exportDownloadCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape  = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename + '.csv'; a.click();
  URL.revokeObjectURL(url);
};

const exportData = async (rows, filename, format) => {
  if (!rows || !rows.length) return;
  if (format === 'csv') { exportDownloadCSV(rows, filename); return; }
  try {
    const XLSX = await import('xlsx');
    const ws   = XLSX.utils.json_to_sheet(rows);
    const wb   = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename + '.xlsx');
  } catch { exportDownloadCSV(rows, filename); }
};

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sa * { box-sizing:border-box; }
  .sa { font-family:'Sora',sans-serif; color:#1a1d2e; animation:saFade .4s ease both; }
  @keyframes saFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sa-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sa-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .sa-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:9px 14px; font-size:.76rem; color:#9a3412; font-weight:600; margin-bottom:1.2rem; }
  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.4rem; width:fit-content; max-width:100%; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .filter-group { display:flex; flex-direction:column; gap:4px; }
  .filter-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; min-width:130px; }
  .filter-select:focus,.filter-input:focus { border-color:#3b6ef8; }
  .date-nav { display:flex; align-items:center; gap:6px; }
  .date-nav-btn { width:32px; height:32px; flex-shrink:0; display:flex; align-items:center; justify-content:center; border:1.5px solid #e0e4f0; border-radius:8px; background:white; cursor:pointer; font-size:1rem; color:#6b7194; }
  .date-nav-btn:hover:not(:disabled) { border-color:#3b6ef8; color:#3b6ef8; }
  .date-nav-btn:disabled { opacity:.4; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .section-head { padding:10px 18px; background:#f8f9fc; border-bottom:1px solid #edf0f9; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:6px; }
  .section-label  { font-size:.8rem; font-weight:700; color:#3b6ef8; }
  .lock-badge     { font-size:.68rem; font-weight:600; padding:3px 8px; border-radius:99px; background:#fffbeb; color:#92400e; border:1px solid #fde68a; white-space:nowrap; }
  .unmarked-badge { font-size:.68rem; font-weight:600; padding:3px 8px; border-radius:99px; background:#fef2f2; color:#991b1b; border:1px solid #fecaca; white-space:nowrap; }
  .att-table { width:100%; border-collapse:collapse; }
  .att-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .att-table th { padding:9px 14px; text-align:center !important; font-size:.64rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .att-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .att-table tbody tr:last-child { border-bottom:none; }
  .att-table tbody tr:hover { background:#fafbff; }
  .att-table td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .att-cards { display:none; padding:10px; }
  .att-card { background:#fafbff; border-radius:10px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .att-card-top  { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .att-card-name { font-size:.84rem; font-weight:700; color:#0f1123; }
  .att-card-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-top:1px; }
  .att-card-row  { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .att-card-row:last-child { border-bottom:none; }
  .att-card-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .status-select { font-family:'Sora',sans-serif; font-size:.76rem; padding:5px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:white; outline:none; cursor:pointer; }
  .status-select.present  { border-color:#a7f3d0; background:#ecfdf5; color:#065f46; }
  .status-select.absent   { border-color:#fecaca; background:#fef2f2; color:#991b1b; }
  .status-select.late     { border-color:#fde68a; background:#fffbeb; color:#92400e; }
  .status-select.on-leave { border-color:#c7d2fe; background:#eef2ff; color:#3730a3; }
  .late-input { width:56px; font-family:'JetBrains Mono',monospace; font-size:.74rem; padding:4px 6px; border:1.5px solid #fde68a; border-radius:6px; background:#fffbeb; text-align:center; outline:none; }
  .stat-bar { display:flex; gap:8px; padding:8px 18px; background:#fafbff; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; }
  .stat-pill { font-size:.72rem; font-weight:600; padding:3px 10px; border-radius:99px; white-space:nowrap; }
  .stat-pill.present  { background:#ecfdf5; color:#10c98e; }
  .stat-pill.absent   { background:#fef2f2; color:#ef4444; }
  .stat-pill.late     { background:#fffbeb; color:#f59e0b; }
  .stat-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .stat-pill.unmarked { background:#f3f4f6; color:#9ca3af; }
  .marked-by { font-size:.66rem; color:#9094a8; font-style:italic; }
  .edited-by { font-size:.66rem; color:#3b6ef8; font-style:italic; }
  .stat-mini { display:inline-flex; align-items:center; gap:4px; font-size:.72rem; font-weight:600; padding:3px 8px; border-radius:99px; }
  .stat-mini.present  { background:#ecfdf5; color:#10c98e; }
  .stat-mini.absent   { background:#fef2f2; color:#ef4444; }
  .stat-mini.late     { background:#fffbeb; color:#f59e0b; }
  .stat-mini.on-leave { background:#eef2ff; color:#3b6ef8; }
  .sa-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .sa-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .yr-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
  .yr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; animation:saFade .35s ease both; }
  .yr-card-name { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:.8rem; display:flex; align-items:center; gap:9px; }
  .yr-stu-avatar { width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#3b6ef8,#6366f1); display:flex; align-items:center; justify-content:center; font-size:.74rem; font-weight:700; color:white; flex-shrink:0; }
  .yr-months { display:grid; grid-template-columns:repeat(6,1fr); gap:4px; }
  .yr-month { text-align:center; }
  .yr-month-lbl { font-size:.58rem; font-weight:600; color:#9094a8; text-transform:uppercase; margin-bottom:3px; }
  .yr-month-bar { height:32px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:.62rem; font-weight:700; }
  .yr-totals { display:flex; gap:8px; flex-wrap:wrap; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f7; align-items:center; }
  .yr-total { font-size:.68rem; font-weight:600; }
  .scroll-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  @media (max-width:768px) {
    .sa-title { font-size:1.1rem; }
    .tab-btn  { padding:6px 10px; font-size:.74rem; }
    .filter-row { flex-direction:column; gap:8px; }
    .filter-group { width:100%; }
    .filter-select,.filter-input { width:100%; min-width:unset; }
    .date-nav { width:100%; }
    .date-nav input.filter-input { flex:1; min-width:0; }
    .att-table-wrap { display:none !important; }
    .att-cards { display:block; }
  }
`;

const todayStr = () => new Date().toISOString().slice(0,10);
const curMonth = () => new Date().toISOString().slice(0,7);
const curYear  = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short'}) : '';

export default function CoordStudentAttendance() {
  const [tab,           setTab]           = useState('daily');
  const [classes,       setClasses]       = useState([]);
  const [filterClass,   setFilterClass]   = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [date,   setDate]   = useState(todayStr());
  const [month,  setMonth]  = useState(curMonth());
  const [year,   setYear]   = useState(curYear());
  const [dailyGroups, setDailyGroups] = useState([]);
  const [monthData,   setMonthData]   = useState(null);
  const [yearData,    setYearData]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [editState,   setEditState]   = useState({});
  const [saving,      setSaving]      = useState(null);
  const [noAccess,    setNoAccess]    = useState(false);

  useEffect(() => {
    api.get('/coordinator/classes').then(r => {
      const raw = r.data?.classes || r.data || [];
      const normalized = raw.map(c => ({
        _id: c._id ?? c.id,
        name: c.name,
        sections: c.sections || [],
      }));
      setClasses(normalized);
    }).catch(() => {});
  }, []);

  const getSections = () => classes.find(c => String(c._id)===String(filterClass))?.sections || [];

  const fetchDaily = useCallback(async () => {
    try {
      setLoading(true); setDailyGroups([]); setNoAccess(false);
      let combos = [];
      if (filterClass && filterSection) {
        const cls = classes.find(c => String(c._id)===String(filterClass));
        combos = [{ classId:filterClass, section:filterSection, className:cls?.name||'' }];
      } else if (filterClass) {
        const cls = classes.find(c => String(c._id)===String(filterClass));
        combos = (cls?.sections||[]).map(s => ({ classId:filterClass, section:s, className:cls?.name||'' }));
      } else {
        classes.forEach(cls => (cls.sections||[]).forEach(s => combos.push({ classId:cls._id, section:s, className:cls.name })));
      }

      let anyForbidden = false;
      const groups = await Promise.all(combos.map(async ({ classId, section, className }) => {
        try {
          const { data } = await api.get(`/coordinator/student-attendance/admin?classId=${classId}&section=${section}&date=${date}`);
          return {
            className, section,
            students:      data.students      || [],
            isLocked:      data.isLocked      || false,
            isHoliday:     data.isHoliday     || false,
            holidayName:   data.holidayName   || null,
            holidayType:   data.holidayType   || null,
            holidayIsPaid: data.holidayIsPaid !== false,
            isWeeklyOff:   data.isWeeklyOff   || false,
            weeklyOffType: data.weeklyOffType  || null,
          };
        } catch (err) {
          if (err?.response?.status === 403) anyForbidden = true;
          return { className, section, students:[], isLocked:false, isHoliday:false, isWeeklyOff:false, weeklyOffType:null, holidayName:null, holidayType:null, holidayIsPaid:true };
        }
      }));

      const visible = groups.filter(g => g.students.length > 0);
      setDailyGroups(visible);
      if (visible.length === 0 && anyForbidden) setNoAccess(true);

      // Seed editState from existing attendance
      const state = {};
      groups.forEach(g => g.students.forEach(s => {
        if (s.attendanceId) state[s.attendanceId] = { status: s.status||'absent', lateMinutes: s.lateMinutes||0 };
      }));
      setEditState(state);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [classes, filterClass, filterSection, date]);

  const fetchMonthly = useCallback(async () => {
    if (!filterClass || !filterSection) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/coordinator/student-attendance/monthly?classId=${filterClass}&section=${filterSection}&month=${month}`);
      setMonthData(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filterClass, filterSection, month]);

  const fetchYearly = useCallback(async () => {
    if (!filterClass || !filterSection) return;
    try {
      setLoading(true);
      const [startY] = year.split('-').map(Number);
      const months = await Promise.all(
        Array.from({ length:12 }, (_, i) => {
          const m = String(i+1).padStart(2,'0');
          return api.get(`/coordinator/student-attendance/monthly?classId=${filterClass}&section=${filterSection}&month=${startY}-${m}`)
            .then(r => ({ month:i+1, students:r.data?.students||[] }))
            .catch(() => ({ month:i+1, students:[] }));
        })
      );
      const studentMap = {};
      months.forEach(({ month:m, students }) => {
        students.forEach(s => {
          if (!studentMap[s.studentId]) studentMap[s.studentId] = { ...s, months:{} };
          studentMap[s.studentId].months[m] = s;
        });
      });
      const { data: yrData } = await api.get(`/coordinator/student-attendance/yearly?classId=${filterClass}&section=${filterSection}&academicYear=${year}`);
      const students = (yrData?.students||[]).map(s => ({ ...s, months: studentMap[s.studentId]?.months||{} }));
      setYearData({ ...yrData, students });
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filterClass, filterSection, year]);

  useEffect(() => {
    if (tab==='daily'   && classes.length>0) fetchDaily();
    if (tab==='monthly' && filterClass && filterSection) fetchMonthly();
    if (tab==='yearly'  && filterClass && filterSection) fetchYearly();
  }, [tab, filterClass, filterSection, date, month, year, classes.length]);

  const changeDate = (delta) => { const d=new Date(date); d.setDate(d.getDate()+delta); setDate(d.toISOString().slice(0,10)); };

  // ── Edit → request (not direct PUT) ──────────────────────────────────────
  const handleCoordEdit = async (attendanceId, field, value, studentInfo) => {
    if (!attendanceId) { toast.error('No record — attendance not marked yet'); return; }
    const current = editState[attendanceId] || {};
    const updated = { ...current, [field]: value };
    if (field === 'status' && value !== 'late') updated.lateMinutes = 0;
    setEditState(prev => ({ ...prev, [attendanceId]: updated }));
    try {
      setSaving(attendanceId);
      await api.post('/coordinator/request/student-attendance-edit', {
        attendanceId,
        studentId:   studentInfo?.studentId,
        studentName: studentInfo?.name,
        classId:     filterClass,
        section:     filterSection,
        date,
        status:      updated.status,
        lateMinutes: updated.lateMinutes || 0,
      });
      toast.success('Edit request submitted — awaiting admin approval');
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(null); }
  };

  const summarize = (students) => {
    const s = { present:0, absent:0, late:0, onLeave:0, unmarked:0 };
    students.forEach(st => {
      if (!st.status) s.unmarked++;
      else if (st.status==='present')  s.present++;
      else if (st.status==='absent')   s.absent++;
      else if (st.status==='late')     s.late++;
      else if (st.status==='on-leave') s.onLeave++;
    });
    return s;
  };

  const statusLabel = { present:'✓ Present', absent:'✗ Absent', late:'⏰ Late', 'on-leave':'📋 On Leave' };
  const statusClass = { present:'present', absent:'absent', late:'late', 'on-leave':'on-leave' };

  // StatusSelect — same UI as admin, but onChange fires request
  const StatusSelect = ({ s }) => {
    const ce = editState[s.attendanceId] || {};
    const curStatus = ce.status ?? s.status ?? 'absent';
    const isSaving  = saving === s.attendanceId;

    return (
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <select className={`status-select ${curStatus}`} value={curStatus}
          disabled={isSaving || !s.attendanceId}
          onChange={e => handleCoordEdit(s.attendanceId, 'status', e.target.value, s)}>
          <option value="present">✓ Present</option>
          <option value="absent">✗ Absent</option>
          <option value="late">⏰ Late</option>
          <option value="on-leave">📋 On Leave</option>
        </select>
        {isSaving && <div className="spinner" style={{width:13,height:13}}/>}
        {!s.attendanceId && <span style={{fontSize:'.68rem',color:'#9ca3af'}}>not marked</span>}
      </div>
    );
  };

  // LateCell — same UI as admin, but onChange fires request
  const LateCell = ({ s }) => {
    const ce = editState[s.attendanceId] || {};
    const curStatus = ce.status ?? s.status ?? 'absent';
    if (curStatus === 'late' && s.attendanceId) return (
      <input type="number" min={0} max={120} className="late-input"
        value={ce.lateMinutes ?? s.lateMinutes ?? 0}
        onChange={e => handleCoordEdit(s.attendanceId, 'lateMinutes', Number(e.target.value), s)} />
    );
    return <span style={{color:'#9094a8',fontSize:'.74rem'}}>{s.status==='late'&&s.lateMinutes>0?`${s.lateMinutes}m`:'—'}</span>;
  };

  const handleExport = (fmt) => {
    if (!filterClass) return toast.error('Select a class first');
    if (tab==='daily' && dailyGroups.length) {
      const rows = dailyGroups.flatMap(g => g.students.map(s => ({
        Class: g.className, Section: g.section, Name: s.name,
        'Roll No': s.rollNo||'', 'Admission No': s.admissionNo||'',
        Date: date, Status: s.status||'', 'Late (min)': s.lateMinutes||0,
        'Marked By': s.markedBy||'',
      })));
      exportData(rows, `Student_Attendance_${date}`, fmt);
    } else if (tab==='monthly' && monthData?.students?.length) {
      const rows = monthData.students.map(s => ({
        Name: s.name, 'Roll No': s.rollNo||'', Month: month,
        Present: s.present||0, Absent: s.absent||0, Late: s.late||0,
        'On Leave': s.onLeave||0, 'Total Days': s.totalDays||0,
      }));
      exportData(rows, `Student_Attendance_${month}`, fmt);
    } else if (tab==='yearly' && yearData?.students?.length) {
      const rows = (yearData.students||[]).map(s => {
        const pct = s.totalDays>0 ? Math.round((s.present/s.totalDays)*100) : 0;
        return { Name:s.name, 'Roll No':s.rollNo||'', 'Academic Year':year,
          Present:s.present||0, Absent:s.absent||0, Late:s.late||0,
          'On Leave':s.onLeave||0, 'Total Days':s.totalDays||0, 'Attendance %':`${pct}%` };
      });
      exportData(rows, `Student_Attendance_${filterClass}_${year}`, fmt);
    } else { toast.error('No data to export'); }
  };

  const exportStudent = (s, fmt) => {
    const pct = s.totalDays>0 ? Math.round((s.present/s.totalDays)*100) : 0;
    exportData([{ Name:s.name, 'Roll No':s.rollNo||'', 'Academic Year':year,
      Present:s.present||0, Absent:s.absent||0, Late:s.late||0,
      'On Leave':s.onLeave||0, 'Total Days':s.totalDays||0, 'Attendance %':`${pct}%` }],
      `Attendance_${s.name}_${year}`, fmt);
  };

  return (
    <div className="sa"><style>{style}</style>

      <div className="sa-header">
        <div>
          <div className="sa-title">Student Attendance</div>
          <div className="sa-sub">View and edit attendance marked by teachers — edits require admin approval</div>
        </div>
      </div>

      <div className="req-banner">📋 Attendance edits are submitted as requests — admin must approve before they take effect.</div>

      <div className="tab-row">
        {['daily','monthly','yearly'].map(t => (
          <button key={t} className={`tab-btn ${tab===t?'active':''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <label className="filter-label">Class</label>
          <select className="filter-select" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSection(''); }}>
            <option value="">Select Class</option>
            {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Section</label>
          <select className="filter-select" value={filterSection} onChange={e => setFilterSection(e.target.value)} disabled={!filterClass}>
            <option value="">{filterClass ? 'All Sections' : '—'}</option>
            {sortAsc(getSections()).map(s => <option key={s} value={s}>Section {s}</option>)}
          </select>
        </div>
        {tab==='daily' && (
          <div className="filter-group">
            <label className="filter-label">Date</label>
            <div className="date-nav">
              <button className="date-nav-btn" onClick={() => changeDate(-1)}>←</button>
              <input type="date" className="filter-input" value={date} onChange={e => setDate(e.target.value)} />
              <button className="date-nav-btn" onClick={() => changeDate(1)} disabled={date >= todayStr()}>→</button>
            </div>
          </div>
        )}
        {tab==='monthly' && (
          <div className="filter-group">
            <label className="filter-label">Month</label>
            <input type="month" className="filter-input" value={month} onChange={e => setMonth(e.target.value)} />
          </div>
        )}
        {tab==='yearly' && (
          <div className="filter-group">
            <label className="filter-label">Academic Year</label>
            <input type="text" className="filter-input" value={year} onChange={e => setYear(e.target.value)} placeholder="2025-2026" />
          </div>
        )}
        <div style={{marginLeft:'auto',display:'flex',gap:5,alignItems:'flex-end'}}>
          <button onClick={() => handleExport('csv')}
            style={{display:'inline-flex',alignItems:'center',gap:5,padding:'7px 14px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'white',fontFamily:'Sora',fontSize:'.76rem',fontWeight:600,cursor:'pointer',color:'#6b7194'}}>
            <ArrowDownTrayIcon style={{width:14,height:14,strokeWidth:2}}/>CSV
          </button>
          <button onClick={() => handleExport('xlsx')}
            style={{display:'inline-flex',alignItems:'center',gap:5,padding:'7px 14px',border:'1.5px solid #3b6ef8',borderRadius:9,background:'#eef2ff',fontFamily:'Sora',fontSize:'.76rem',fontWeight:600,cursor:'pointer',color:'#3b6ef8'}}>
            <ArrowDownTrayIcon style={{width:14,height:14,strokeWidth:2}}/>XLSX
          </button>
        </div>
      </div>

      {loading ? (
        <div className="sa-loading"><div className="spinner"/>Loading…</div>
      ) : (
        <>
          {/* ── DAILY ── */}
          {tab==='daily' && (
            noAccess ? (
              <div className="sa-empty">You do not have access to this class's attendance.</div>
            ) : dailyGroups.length === 0 ? (
              <div className="sa-empty">No attendance found for {new Date(date+'T00:00:00').toLocaleDateString('en-PK',{day:'numeric',month:'long',year:'numeric'})}.</div>
            ) : dailyGroups.map((group, gi) => {
              const sm = summarize(group.students);
              return (
                <div key={gi} className="panel" style={{marginBottom:'1.2rem'}}>
                  <div className="section-head">
                    <div className="section-label">
                      {group.className} — Section {group.section}
                      <span style={{fontWeight:400,color:'#9094a8',marginLeft:8,fontSize:'.74rem'}}>{group.students.length} students</span>
                    </div>
                    {group.isLocked
                      ? <span className="lock-badge">🔒 Submitted</span>
                      : <span className="unmarked-badge">⚠ Not submitted</span>}
                  </div>

                  {/* Holiday banner */}
                  {group.isHoliday && (() => {
                    const cfg = {
                      public:    { icon:'🎉', label:'Public Holiday',    bg:'#fef2f2', border:'#fecaca', color:'#dc2626' },
                      school:    { icon:'🏫', label:'School Holiday',    bg:'#eef2ff', border:'#c7d2fe', color:'#3b6ef8' },
                      emergency: { icon:'⚠️', label:'Emergency Closure', bg:'#fffbeb', border:'#fde68a', color:'#d97706' },
                    };
                    const c = cfg[group.holidayType] || cfg.public;
                    return (
                      <div style={{background:c.bg,border:`1px solid ${c.border}`,borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',gap:10,fontSize:'.82rem',fontWeight:600,color:c.color}}>
                        <span style={{fontSize:'1.1rem'}}>{c.icon}</span>
                        <div>
                          <div>{c.label} — {group.holidayName}</div>
                          <div style={{fontWeight:400,fontSize:'.72rem',marginTop:1,color:c.color+'cc'}}>
                            {group.holidayIsPaid ? 'Paid holiday — No attendance required' : 'Unpaid closure — No attendance required'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Weekly off banner */}
                  {group.isWeeklyOff && !group.isHoliday && (() => {
                    const isSat = group.weeklyOffType === 'saturday';
                    return (
                      <div style={{background:'#f8f9fc',border:'1px solid #e0e4f0',borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',gap:10,fontSize:'.82rem',fontWeight:600,color:'#6b7194'}}>
                        <span style={{fontSize:'1.1rem'}}>{isSat ? '📅' : '🏖️'}</span>
                        <div>
                          <div>{isSat ? 'Saturday Off' : 'Weekly Off Day'} — No attendance</div>
                          <div style={{fontWeight:400,fontSize:'.72rem',marginTop:1,color:'#9094a8'}}>
                            {isSat ? 'Saturday policy — off day' : 'Weekly recurring off day'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="stat-bar">
                    {sm.present >0&&<span className="stat-pill present">✓ Present: {sm.present}</span>}
                    {sm.absent  >0&&<span className="stat-pill absent">✗ Absent: {sm.absent}</span>}
                    {sm.late    >0&&<span className="stat-pill late">⏰ Late: {sm.late}</span>}
                    {sm.onLeave >0&&<span className="stat-pill on-leave">📋 Leave: {sm.onLeave}</span>}
                    {sm.unmarked>0&&<span className="stat-pill unmarked">? Unmarked: {sm.unmarked}</span>}
                  </div>

                  {/* Desktop table */}
                  <div className="att-table-wrap" style={{overflowX:'auto'}}>
                    <table className="att-table">
                      <thead><tr><th>#</th><th>Student</th><th>Status</th><th>Late Min</th><th>Marked By</th></tr></thead>
                      <tbody>
                        {group.students.map((s, i) => (
                          <tr key={s.studentId}>
                            <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.72rem'}}>{i+1}</td>
                            <td>
                              <div style={{fontWeight:600,fontSize:'.82rem'}}>{s.name}</div>
                              <div style={{fontSize:'.7rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>Roll: {s.rollNo}</div>
                            </td>
                            <td>
                              {(s.isHoliday||s.status==='holiday') ? (
                                <span style={{fontSize:'.72rem',fontWeight:700,padding:'3px 9px',borderRadius:99,
                                  background:s.holidayType==='school'?'#eef2ff':s.holidayType==='emergency'?'#fffbeb':'#fef2f2',
                                  color:s.holidayType==='school'?'#3b6ef8':s.holidayType==='emergency'?'#d97706':'#dc2626'}}>
                                  {s.holidayType==='school'?'🏫 School Holiday':s.holidayType==='emergency'?'⚠️ Emergency':'🎉 Public Holiday'}
                                </span>
                              ) : (s.isWeeklyOff||s.status==='off') ? (
                                <span style={{fontSize:'.72rem',fontWeight:600,padding:'3px 9px',borderRadius:99,background:'#f0f2f7',color:'#6b7194'}}>
                                  {group.weeklyOffType==='saturday'?'📅 Saturday Off':'🏖️ Weekly Off'}
                                </span>
                              ) : <StatusSelect s={s}/>}
                            </td>
                            <td><LateCell s={s}/></td>
                            <td>
                              {s.markedBy&&<div className="marked-by">🖊 {s.markedBy}</div>}
                              {s.editedBy&&<div className="edited-by">✏ Admin: {s.editedBy} · {fmtDate(s.editedAt)}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="att-cards">
                    {group.students.map((s, i) => (
                      <div key={s.studentId} className="att-card">
                        <div className="att-card-top">
                          <div>
                            <div className="att-card-name">{s.name}</div>
                            <div className="att-card-roll">Roll: {s.rollNo}</div>
                          </div>
                          <span style={{fontSize:'.68rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>#{i+1}</span>
                        </div>
                        <div className="att-card-row"><span className="att-card-label">Status</span><StatusSelect s={s}/></div>
                        <div className="att-card-row"><span className="att-card-label">Late Min</span><LateCell s={s}/></div>
                        {(s.markedBy||s.editedBy) && (
                          <div className="att-card-row"><span className="att-card-label">By</span>
                            <div style={{textAlign:'right'}}>
                              {s.markedBy&&<div className="marked-by">🖊 {s.markedBy}</div>}
                              {s.editedBy&&<div className="edited-by">✏ {s.editedBy}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* ── MONTHLY ── */}
          {tab==='monthly' && (
            !filterClass || !filterSection ? (
              <div className="sa-empty">Select a class and section to view monthly attendance.</div>
            ) : !monthData ? null : (
              <div className="panel">
                <div className="panel-head">
                  <div className="panel-title">{classes.find(c=>String(c._id)===String(filterClass))?.name} Section {filterSection} — {month}</div>
                </div>
                <div className="scroll-table-wrap">
                  <table className="att-table">
                    <thead><tr><th>Student</th><th>Present</th><th>Absent</th><th>Late</th><th>On Leave</th><th>Total</th></tr></thead>
                    <tbody>
                      {monthData.students?.map(s => (
                        <tr key={s.studentId}>
                          <td>
                            <div style={{fontWeight:600,fontSize:'.82rem'}}>{s.name}</div>
                            <div style={{fontSize:'.7rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>Roll: {s.rollNo}</div>
                          </td>
                          <td><span className="stat-mini present">{s.present}</span></td>
                          <td><span className="stat-mini absent">{s.absent}</span></td>
                          <td><span className="stat-mini late">{s.late}</span></td>
                          <td><span className="stat-mini on-leave">{s.onLeave}</span></td>
                          <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>{s.totalDays}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}

          {/* ── YEARLY ── */}
          {tab==='yearly' && (
            !filterClass || !filterSection ? (
              <div className="sa-empty">Select a class and section to view yearly attendance.</div>
            ) : !yearData ? null : (
              <>
                <div style={{marginBottom:'1rem',fontSize:'.88rem',fontWeight:700,color:'#0f1123'}}>
                  {classes.find(c=>String(c._id)===String(filterClass))?.name} · Section {filterSection} · {year}
                  <span style={{fontSize:'.74rem',fontWeight:500,color:'#9094a8',marginLeft:8}}>{yearData.students?.length||0} students</span>
                </div>
                <div className="yr-grid">
                  {yearData.students?.map((s, si) => {
                    const pct = s.totalDays>0 ? Math.round((s.present/s.totalDays)*100) : 0;
                    const ini = (s.name||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
                    return (
                      <div key={s.studentId} className="yr-card" style={{animationDelay:si*.03+'s'}}>
                        <div className="yr-card-name">
                          <div className="yr-stu-avatar">{ini}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div>{s.name}</div>
                            <div style={{fontSize:'.7rem',fontWeight:400,color:'#9094a8'}}>Roll: {s.rollNo}</div>
                          </div>
                          <div style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.9rem',fontWeight:700,
                            color:pct>=75?'#10c98e':pct>=50?'#f59e0b':'#ef4444'}}>{pct}%</div>
                        </div>
                        <div className="yr-months">
                          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((lbl, i) => {
                            const m   = s.months?.[i+1] || {};
                            const tot = (m.present||0)+(m.absent||0)+(m.late||0)+(m.onLeave||0);
                            const mp  = tot>0 ? Math.round(((m.present||0)+(m.late||0))/tot*100) : null;
                            const bg  = mp===null?'#f0f2f7':mp>=90?'#ecfdf5':mp>=70?'#fffbeb':mp>0?'#fef2f2':'#f0f2f7';
                            const col = mp===null?'#c0c5dc':mp>=90?'#10c98e':mp>=70?'#f59e0b':mp>0?'#ef4444':'#c0c5dc';
                            return (
                              <div key={lbl} className="yr-month">
                                <div className="yr-month-lbl">{lbl}</div>
                                <div className="yr-month-bar" style={{background:bg,color:col}}
                                  title={tot>0?`P:${m.present||0} A:${m.absent||0} L:${m.late||0}`:'No data'}>
                                  {mp!==null?mp+'%':'—'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="yr-totals">
                          <span className="yr-total" style={{color:'#10c98e'}}>✓ {s.present} present</span>
                          <span className="yr-total" style={{color:'#ef4444'}}>✗ {s.absent} absent</span>
                          <span className="yr-total" style={{color:'#f59e0b'}}>◑ {s.late} late</span>
                          <span className="yr-total" style={{color:'#6b7194'}}>✈ {s.onLeave} leave</span>
                          <span style={{marginLeft:'auto',display:'flex',gap:3}}>
                            <button onClick={() => exportStudent(s,'csv')}  style={{padding:'3px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                            <button onClick={() => exportStudent(s,'xlsx')} style={{padding:'3px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}
        </>
      )}
    </div>
  );
}